import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const KIND_HEADINGS = {
  attribute_set: "Set resource attributes",
  policy_add_statement: "Add IAM policy statement",
  singleton_merge: "Update account-level policy",
  resource_remove: "Remove from resource",
  resource_create: "Create new resource",
  plan: "Multi-step remediation plan",
};

const formatKind = (kind) =>
  KIND_HEADINGS[kind] || String(kind || "Planned change").replace(/_/g, " ");

const formatValue = (v) => {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const JsonPre = ({ data, maxHeight = 160 }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1,
      fontSize: 11,
      lineHeight: 1.4,
      backgroundColor: "#fff",
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      overflow: "auto",
      maxHeight,
    }}
  >
    {JSON.stringify(data, null, 2)}
  </Box>
);

const FieldSetTable = ({ fields, emptyLabel = "No fields listed." }) => {
  if (!fields?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1, backgroundColor: "#fff" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "grey.50" }}>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Target value</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((f, i) => (
            <TableRow key={i}>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{f.field || "—"}</TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{formatValue(f.target_value)}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{f.description || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const IntentBlock = ({ intent, depth = 0, showStepLabels = true }) => {
  if (!intent || typeof intent !== "object") return null;

  const kind = intent.kind;
  const sx = { mb: depth > 0 ? 1.5 : 0 };

  if (kind === "plan" && Array.isArray(intent.steps)) {
    return (
      <Box sx={sx}>
        {intent.steps.map((step, i) => (
          <Box
            key={i}
            sx={{
              mb: i < intent.steps.length - 1 ? 1.5 : 0,
              pl: depth > 0 ? 1.5 : 0,
              borderLeft: depth > 0 ? 2 : 0,
              borderColor: "divider",
            }}
          >
            {showStepLabels && (
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
                Step {i + 1}: {formatKind(step?.kind)}
              </Typography>
            )}
            <IntentBlock intent={step} depth={depth + 1} showStepLabels={showStepLabels} />
          </Box>
        ))}
      </Box>
    );
  }

  if (kind === "attribute_set") {
    return (
      <Box sx={sx}>
        <FieldSetTable fields={intent.fields} />
      </Box>
    );
  }

  if (kind === "singleton_merge") {
    return (
      <Box sx={sx}>
        {intent.document_kind && (
          <Typography variant="body2" sx={{ fontSize: 13, mb: 1 }}>
            Document: <strong>{String(intent.document_kind).replace(/_/g, " ")}</strong>
          </Typography>
        )}
        <FieldSetTable fields={intent.fields_changing} emptyLabel="No field changes listed." />
      </Box>
    );
  }

  if (kind === "policy_add_statement") {
    return (
      <Box sx={sx}>
        {intent.document_type && (
          <Typography variant="body2" sx={{ fontSize: 13, mb: intent.statement_template ? 0.75 : 0 }}>
            Policy type: <strong>{String(intent.document_type).replace(/_/g, " ")}</strong>
          </Typography>
        )}
        {intent.statement_template && <JsonPre data={intent.statement_template} />}
      </Box>
    );
  }

  if (kind === "resource_remove") {
    return (
      <Box sx={sx}>
        <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.5 }}>
          {intent.criteria_description || "Remove a specific item from the resource."}
          {intent.target_kind ? (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ({intent.target_kind.replace(/_/g, " ")})
            </Typography>
          ) : null}
        </Typography>
      </Box>
    );
  }

  if (kind === "resource_create") {
    return (
      <Box sx={sx}>
        {intent.resource_type && (
          <Typography variant="body2" sx={{ fontSize: 13, mb: 0.75 }}>
            Resource type: <strong>{intent.resource_type}</strong>
          </Typography>
        )}
        {intent.create_payload_template && <JsonPre data={intent.create_payload_template} />}
      </Box>
    );
  }

  return <JsonPre data={intent} maxHeight={120} />;
};

const SECTION_TITLE = "What CloudCatcher does";

/**
 * Structured intent from GET /remediation/checks/{check_id} — collapsed by default.
 * Summary shows intent_prose; expanded panel shows technical detail only (no repeat).
 */
const PlaybookIntent = ({ intent, intentProse }) => {
  const prose = typeof intentProse === "string" ? intentProse.trim() : "";
  const hasIntent = intent && typeof intent === "object";

  if (!prose && !hasIntent) return null;

  const summaryLine = prose || (hasIntent ? formatKind(intent.kind) : "");
  const hasExpandableDetail = hasIntent;

  return (
    <Accordion
      defaultExpanded={false}
      disableGutters
      elevation={0}
      sx={{
        mb: 0,
        backgroundColor: "#fff",
        border: 1,
        borderColor: "divider",
        borderRadius: "8px !important",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={hasExpandableDetail ? <ExpandMoreIcon /> : null}
        sx={{
          minHeight: 44,
          alignItems: "flex-start",
          px: 1.25,
          py: 1,
          "& .MuiAccordionSummary-content": { my: 0, alignItems: "flex-start" },
          "& .MuiAccordionSummary-expandIconWrapper": { mt: 0.25, alignSelf: "flex-start" },
        }}
      >
        <Box sx={{ minWidth: 0, pr: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", lineHeight: 1.3 }}>
            {SECTION_TITLE}
          </Typography>
          {summaryLine && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.5,
                fontSize: 13,
                lineHeight: 1.45,
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {summaryLine}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      {hasExpandableDetail && (
        <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25, borderTop: 1, borderColor: "divider" }}>
          <IntentBlock intent={intent} showStepLabels={!prose} />
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default PlaybookIntent;
